import { useEffect, useRef, useState } from "react";
import { Minus, Play, Plus, Square, Trash2 } from "lucide-react";
import { DRUMS } from "../../lib/instruments";

const STEPS = 16;
const row = (on) => Array.from({ length: STEPS }, (_, i) => on.includes(i));
const PRESETS = {
  Rock: { kick: [0, 8, 10], snare: [4, 12], clap: [], hat: [0, 2, 4, 6, 8, 10, 12, 14], open: [], tom: [14, 15] },
  "Hip hop": { kick: [0, 3, 8, 11], snare: [4, 12], clap: [12], hat: [0, 2, 4, 6, 8, 10, 12, 14], open: [7], tom: [] },
  House: { kick: [0, 4, 8, 12], snare: [], clap: [4, 12], hat: [], open: [2, 6, 10, 14], tom: [] },
  Funk: { kick: [0, 3, 6, 10], snare: [4, 12, 15], clap: [], hat: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15], open: [], tom: [] },
};
const fromPreset = (p) => Object.fromEntries(DRUMS.map((d) => [d.id, row(p[d.id] || [])]));

export const DrumMachine = ({ inst, disabled }) => {
  const [grid, setGrid] = useState(() => fromPreset(PRESETS.Rock));
  const [bpm, setBpm] = useState(100);
  const [on, setOn] = useState(false);
  const [step, setStep] = useState(-1);
  const live = useRef({ grid, bpm });
  live.current = { grid, bpm };

  useEffect(() => {
    if (!on) return;
    const c = inst.ctx;
    c.resume();
    let next = c.currentTime + 0.06;
    let n = 0;
    const timers = [];
    const iv = setInterval(() => {
      while (next < c.currentTime + 0.12) {
        const s = n % STEPS;
        DRUMS.forEach((d) => live.current.grid[d.id][s] && inst.drum(d.id, next));
        timers.push(setTimeout(() => setStep(s), Math.max(0, (next - c.currentTime) * 1000)));
        next += 60 / live.current.bpm / 4;
        n++;
      }
    }, 25);
    return () => { clearInterval(iv); timers.forEach(clearTimeout); setStep(-1); };
  }, [on, inst]);

  const toggle = (id, i) => setGrid((g) => ({ ...g, [id]: g[id].map((v, j) => (j === i ? !v : v)) }));
  return (
    <div data-testid="drum-machine">
      <div className="flex flex-wrap items-center gap-2">
        <button disabled={disabled} onClick={() => setOn((v) => !v)} data-testid="drum-play-btn"
          className={`flex items-center gap-2 rounded-[3px] px-4 py-2 text-sm font-bold transition-colors disabled:opacity-40 ${on ? "bg-pink-500 text-white" : "bg-yellow-400 text-black hover:bg-yellow-300"}`}>
          {on ? <Square className="h-4 w-4" /> : <Play className="h-4 w-4" />} {on ? "Stop" : "Play"}
        </button>
        <div className="flex items-center gap-1 rounded-[3px] border border-zinc-800 bg-black px-1">
          <button onClick={() => setBpm((b) => Math.max(60, b - 1))} data-testid="drum-bpm-down-btn" className="p-1.5 text-zinc-400 hover:text-white"><Minus className="h-3.5 w-3.5" /></button>
          <span className="mono text-sm w-16 text-center" data-testid="drum-bpm-value">{bpm} BPM</span>
          <button onClick={() => setBpm((b) => Math.min(180, b + 1))} data-testid="drum-bpm-up-btn" className="p-1.5 text-zinc-400 hover:text-white"><Plus className="h-3.5 w-3.5" /></button>
        </div>
        <input type="range" min={60} max={180} value={bpm} onChange={(e) => setBpm(Number(e.target.value))} data-testid="drum-bpm-slider" className="w-28 accent-yellow-400" />
        <div className="flex flex-wrap gap-1.5">
          {Object.keys(PRESETS).map((p) => (
            <button key={p} onClick={() => setGrid(fromPreset(PRESETS[p]))} data-testid={`drum-preset-${p.replace(" ", "-").toLowerCase()}`}
              className="rounded-[3px] border border-zinc-800 px-2.5 py-1 mono text-[10px] uppercase text-zinc-300 hover:border-yellow-400 hover:text-yellow-300">{p}</button>
          ))}
          <button onClick={() => setGrid(fromPreset({}))} data-testid="drum-clear-btn"
            className="flex items-center gap-1 rounded-[3px] border border-zinc-800 px-2.5 py-1 mono text-[10px] uppercase text-zinc-400 hover:border-pink-400 hover:text-pink-300"><Trash2 className="h-3 w-3" /> Clear</button>
        </div>
      </div>
      <div className="mt-4 overflow-x-auto no-scrollbar">
        <div className="min-w-[640px] space-y-1.5">
          {DRUMS.map((d) => (
            <div key={d.id} className="flex items-center gap-2">
              <button onPointerDown={() => !disabled && (inst.ctx.resume(), inst.drum(d.id))} data-testid={`drum-pad-${d.id}`}
                className="w-20 shrink-0 rounded-[3px] border border-zinc-700 bg-zinc-900 py-1.5 mono text-[10px] uppercase text-zinc-300 hover:border-yellow-400 active:bg-yellow-400 active:text-black">{d.label}</button>
              <div className="grid flex-1 grid-cols-16 gap-1" style={{ gridTemplateColumns: `repeat(${STEPS}, minmax(0, 1fr))` }}>
                {grid[d.id].map((v, i) => (
                  <button key={i} onClick={() => toggle(d.id, i)} data-testid={`drum-step-${d.id}-${i + 1}`} aria-pressed={v}
                    className={`h-7 rounded-[2px] border transition-colors ${v ? "bg-yellow-400 border-yellow-300" : i % 4 === 0 ? "bg-zinc-800 border-zinc-700" : "bg-zinc-900 border-zinc-800"} ${step === i ? "ring-2 ring-[#39ff6a]" : ""}`} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
